/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {DISMISS_MSG} from '../../../common/constants';
import {AutocompleteFilterComponent} from '../../../core/filters/autocomplete-filter/autocomplete-filter.component';
import {StateService} from '../../../core/state/state.service';
import {SubregionService} from '../../../services/subregion.service';
import {AddSubregionPopUpComponent} from './add-subregion-pop-up/add-subregion-pop-up.component';

@Component({
    selector: 'app-manage-subregions',
    templateUrl: './manage-subregions.component.html',
    styleUrl: './manage-subregions.component.scss',
    standalone: false
})
export class ManageSubregionsComponent implements OnInit {
  showSpinner: boolean;
  subregions: any[];
  subregionColumns: ColDef[] = [
    {field: 'subregionValue', headerName: 'Subregion', filter: AutocompleteFilterComponent},
    {field: 'region', headerName: 'Region', filter: AutocompleteFilterComponent},
  ];
  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitGridWidth",
  };

  constructor(private readonly subregionService: SubregionService,
              public dialog: MatDialog,
              public snackBar: FabMessageService,
              private readonly stateService: StateService) {
  }

  ngOnInit() {
    this.getSubregions();
  }

  /**
   * Gets all the subregions from the backend
   */
  getSubregions() {
    this.showSpinner = true
    this.subregionService.getSubregions().subscribe({
        next: (response) => {
          this.subregions = response;
        },
        error: () => {
          this.snackBar.displayError('Error loading subregions', DISMISS_MSG);
          this.showSpinner = false;
        },
        complete: () => {
          this.showSpinner = false;
        }
      }
    );
  }

  /**
   * When the add button is clicked, open the pop up to add a new subregion
   */
  onAddButtonClick() {
    const dialogRef = this.dialog.open(AddSubregionPopUpComponent);
    dialogRef.afterClosed().subscribe(res => {
      // received data from dialog-component
      if (res) {
        this.addSubregion(res.data.subregionName, res.data.region);
      }
    });
  }

  /**
   * Adds a new subregion to the database
   * @param subregion the subregion name to add
   * @param region the region to put the new subregion under
   */
  addSubregion(subregion: string, region: string) {
    this.showSpinner = true;
    this.subregionService.addSubregion(subregion, region).subscribe({
      error: (err) => {
        this.showSpinner = false;
        this.snackBar.displayError(err, DISMISS_MSG);
      },
      complete: () => {
        this.snackBar.displaySuccess(`Added new subregion`, DISMISS_MSG);
        this.getSubregions();
      }
    });
  }
}
